import React, { useState, useEffect } from 'react';
import { X, Save } from 'lucide-react';
import { Api } from '../../services/Api/Api';

const UpdateUserModal = ({ isOpen, onClose, onSuccess, user }) => {
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        address: '',
        password: '',
        company: ''
    });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen && user) {
            setFormData({
                first_name: user.first_name || '',
                last_name: user.last_name || '',
                email: user.email || '',
                phone: user.phone || '',
                address: user.address || '',
                password: '', // Ne pas pré-remplir le mot de passe
                company: user.company?.name || ''
            });
        }
    }, [isOpen, user]);

    // Réinitialiser le formulaire
    const resetForm = () => {
        setFormData({
            first_name: '',
            last_name: '',
            email: '',
            phone: '',
            address: '',
            password: '',
            company: ''
        });
    };

    // Gérer les changements dans le formulaire
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Modifier un utilisateur existant
    const updateUser = async () => {
        try {
            setLoading(true);

            console.log('Début de la modification utilisateur...');

            const payload = {
                email: formData.email,
                first_name: formData.first_name,
                last_name: formData.last_name,
                phone: formData.phone,
                address: formData.address
            };

            // Ajouter le mot de passe seulement s'il est fourni
            if (formData.password.trim()) {
                payload.password = formData.password;
            }

            // Ajouter company seulement si elle est fournie
            if (formData.company.trim()) {
                payload.company = formData.company;
            }


            const response = await Api.put(`formalite/users-formalite/${user.id}`, payload);

            resetForm();
            onSuccess('Utilisateur modifié avec succès !');

        } catch (error) {
            console.error('Erreur lors de la modification:', error);
            console.error('Détails de l\'erreur:', {
                message: error.message,
                status: error.status,
                response: error.response
            });
            alert('Erreur lors de la modification de l\'utilisateur: ' + (error.message || 'Erreur inconnue'));
        } finally {
            setLoading(false);
        }
    };

    // Gérer la soumission du formulaire
    const handleSubmit = async (e) => {
        // CRITIQUE: Empêcher le comportement par défaut
        e.preventDefault();
        e.stopPropagation();

        console.log('Soumission du formulaire de modification interceptée');

        // Validation simple
        if (!formData.first_name || !formData.last_name || !formData.email) {
            alert('Veuillez remplir tous les champs obligatoires (prénom, nom, email)');
            return;
        }

        // Validation email
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            alert('Veuillez entrer un email valide');
            return;
        }

        await updateUser();
    };

    // Gérer la fermeture du modal
    const handleClose = () => {
        if (!loading) {
            resetForm();
            onClose();
        }
    };

    // Gérer la touche Échap
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isOpen && !loading) {
                handleClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
            return () => document.removeEventListener('keydown', handleKeyDown);
        }
    }, [isOpen, loading]);

    if (!isOpen || !user) return null;

    return (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && handleClose()}>
            <div className="modal-content">
                <div className="modal-header">
                    <h3>Modifier l'utilisateur</h3>
                    <button
                        type="button"
                        className="modal-close"
                        onClick={handleClose}
                        disabled={loading}
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Utilisation d'une div au lieu d'un form */}
                <div className="modal-form">
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="first_name">Prénom *</label>
                            <input
                                type="text"
                                id="first_name"
                                name="first_name"
                                value={formData.first_name}
                                onChange={handleInputChange}
                                required
                                disabled={loading}
                                placeholder="Prénom de l'utilisateur"
                                autoComplete="given-name"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="last_name">Nom *</label>
                            <input
                                type="text"
                                id="last_name"
                                name="last_name"
                                value={formData.last_name}
                                onChange={handleInputChange}
                                required
                                disabled={loading}
                                placeholder="Nom de l'utilisateur"
                                autoComplete="family-name"
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="email">Email *</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                required
                                disabled={loading}
                                placeholder="email@exemple.com"
                                autoComplete="email"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="phone">Téléphone</label>
                            <input
                                type="tel"
                                id="phone"
                                name="phone"
                                value={formData.phone}
                                onChange={handleInputChange}
                                disabled={loading}
                                placeholder="+2250700000000"
                                autoComplete="tel"
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="password">
                                Mot de passe
                                <span className="password-hint">(laisser vide pour ne pas changer)</span>
                            </label>
                            <input
                                type="password"
                                id="password"
                                name="password"
                                value={formData.password}
                                onChange={handleInputChange}
                                disabled={loading}
                                placeholder="Nouveau mot de passe"
                                autoComplete="new-password"
                                minLength="6"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="company">Entreprise</label>
                            <input
                                type="text"
                                id="company"
                                name="company"
                                value={formData.company}
                                onChange={handleInputChange}
                                disabled={loading}
                                placeholder="Nom de l'entreprise"
                                autoComplete="organization"
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="address">Adresse</label>
                        <textarea
                            id="address"
                            name="address"
                            value={formData.address}
                            onChange={handleInputChange}
                            rows="3"
                            disabled={loading}
                            placeholder="Adresse complète"
                            autoComplete="address-line1"
                        />
                    </div>

                    {/* Informations supplémentaires en lecture seule */}
                    <div className="user-info-section">
                        <h4>Informations du compte</h4>
                        <div className="info-grid">
                            <div className="info-item">
                                <span className="info-label">ID:</span>
                                <span className="info-value">{user.id}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Statut:</span>
                                <span className={`info-value ${user.is_active ? 'status-active' : 'status-inactive'}`}>
                                    {user.is_active ? 'Actif' : 'Inactif'}
                                </span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Rôle:</span>
                                <span className="info-value">
                                    {user.is_staff ? 'Administrateur' : 'Utilisateur'}
                                </span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Créé le:</span>
                                <span className="info-value">
                                    {new Date(user.date_joined).toLocaleDateString('fr-FR')}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="modal-actions">
                        <button
                            type="button"
                            className="cancel-button"
                            onClick={handleClose}
                            disabled={loading}
                        >
                            Annuler
                        </button>
                        <button
                            type="button"
                            className="save-button"
                            onClick={handleSubmit}
                            disabled={loading}
                        >
                            <Save size={16} />
                            {loading ? 'Modification en cours...' : 'Sauvegarder'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UpdateUserModal;